import { inject, Injectable } from '@angular/core';
import { Photographer } from '../models/new/photographer';
import {
  ColorShots,
  Order,
  OrderDetail,
  OrderFilters,
  OrderStatus,
  OrderType,
  OrderTypeEnum,
  Waybill
} from '../models/new/order';
import { Status } from '../models/new/status';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { DateRange } from '../models/new/dateRange';
import { AspectRatio, MediaFormat, Shot, ShotSource, ShotType } from '../models/new/shot';

import { Color, Size, SizeGroup } from '../models/new/style';
import { Warehouse } from 'src/app/shared/types/warehouse.model';
import moment from 'moment';
import { StyleMapper } from './style-mapper';
import { OrderTypeMapper } from './order-type-mapper';
@Injectable({
  providedIn: 'root'
})
export class OrderMapper {
  private readonly orderTypeMapper = inject(OrderTypeMapper);

  constructor(
    private utils: UtilitiesService,
    private styleMapper: StyleMapper
  ) {}

  //da unificare
  toOrderSearch(order: any) {
    return new Order({
      id: order.orderId,
      basicNetId: order.basicNetId ?? undefined,
      status: new Status({ id: order.orderStatusId, name: order.orderStatusName }),
      dueDate: order.dueDate ? new Date(order.dueDate) : undefined,
      finalCost: order.finalCost,
      //estimatedCost: order.estimatedCost,
      totalOrdered: order.totalOrdered != null ? Number(order.totalOrdered) : undefined,
      totalShipped: order.totalShipped != null ? Number(order.totalShipped) : undefined,
      creationDate: order.creationDate ? new Date(order.creationDate) : undefined,
      completionDate: order.completionDate ? new Date(order.completionDate) : undefined,
      photographer: order.photographerPersonId
        ? new Photographer({ id: order.photographerPersonId, fullName: order.photographerFullName })
        : undefined,
      notes: order.notes,
      orderType: order.orderType ? this.orderTypeMapper.toOrderTypeEnum(order.orderType.id) : undefined,
      warehouse: order.warehouse
        ? new Warehouse({
            id: order.warehouse.id,
            code: order.warehouse.code,
            description: order.warehouse.description,
            address: order.warehouse.address
          })
        : undefined
    });
  }

  toOrder(order: any) {
    return new Order({
      id: order.orderId,
      basicNetId: order.basicNetId ?? undefined,
      status: new Status({ id: order.orderStatusId as OrderStatus, name: order.orderStatusName }),
      dueDate: order.dueDate ? new Date(order.dueDate) : undefined,
      finalCost: order.finalCost,
      //estimatedCost: order.estimatedCost,
      totalOrdered: order.totalOrdered != null ? Number(order.totalOrdered) : undefined,
      totalShipped: order.totalShipped != null ? Number(order.totalShipped) : undefined,
      creationDate: order.creationDate ? new Date(order.creationDate) : undefined,
      completionDate: order.completionDate ? new Date(order.completionDate) : undefined,
      photographer: order.photographerPersonId
        ? new Photographer({ id: order.photographerPersonId, fullName: order.photographerFullName })
        : undefined,
      notes: order.notes,
      orderType: order.orderType ? this.orderTypeMapper.toOrderTypeEnum(order.orderType.id) : undefined,
      warehouse: order.warehouse
        ? new Warehouse({
            id: order.warehouse.id,
            code: order.warehouse.code,
            description: order.warehouse.description,
            address: order.warehouse.address
          })
        : undefined,
      waybill: order.waybill
        ? new Waybill({
            waybillNumber: order.waybill.waybillNumber,
            waybillUrl: order.waybill.waybillUrl,
            shippingDate: order.waybill.shippingDate ? new Date(order.waybill.shippingDate) : undefined
          })
        : undefined,
      isFake: order.isFake
    });
  }

  toWhereClause(filters: OrderFilters) {
    return {
      orderId: filters.ids ? { in: filters.ids } : undefined,
      dueDate: filters.dueDate
        ? {
            gte: moment(filters.dueDate.from).format('YYYY-MM-DD'),
            lte: moment(filters.dueDate.to).format('YYYY-MM-DD')
          }
        : undefined,
      creationDate: filters.creationDate
        ? {
            gte: moment(filters.creationDate.from).startOf('day').toDate(),
            lte: moment(filters.creationDate.to).endOf('day').toDate()
          }
        : undefined,
      completionDate: filters.completionDate
        ? {
            gte: moment(filters.completionDate.from).startOf('day').toDate(),
            lte: moment(filters.completionDate.to).endOf('day').toDate()
          }
        : undefined,
      photographerPersonId: filters.photographers?.length ? { in: filters.photographers.map((p) => p.id) } : undefined,
      orderStatusId: filters.statuses?.length ? { in: filters.statuses.map((s) => s.id) } : undefined,
      orderType: filters.orderTypes?.length
        ? { id: { in: filters.orderTypes.map((orderType) => Number(orderType.id)) } }
        : undefined,
      warehouse: filters.warehouses?.length ? { id: { in: filters.warehouses.map((w) => w.id) } } : undefined
    };
  }

  //da uniformare
  toWhereClausePhotographer(filters: OrderFilters) {
    return {
      orderId: filters.ids ? { in: filters.ids } : undefined,
      dueDate: filters.dueDate
        ? {
            gte: moment(filters.dueDate.from).format('YYYY-MM-DD'),
            lte: moment(filters.dueDate.to).format('YYYY-MM-DD')
          }
        : undefined,
      creationDate: filters.creationDate
        ? {
            gte: moment(filters.creationDate.from).startOf('day').toDate(),
            lte: moment(filters.creationDate.to).endOf('day').toDate()
          }
        : undefined,
      completionDate: filters.completionDate
        ? {
            gte: moment(filters.completionDate.from).startOf('day').toDate(),
            lte: moment(filters.completionDate.to).endOf('day').toDate()
          }
        : undefined,
      photographerPersonId: filters.photographers?.length ? { in: filters.photographers.map((p) => p.id) } : undefined,
      orderStatusId: filters.statuses?.length
        ? { in: filters.statuses.map((s) => s.id).filter((id) => id !== OrderStatus.Draft) }
        : { neq: OrderStatus.Draft },
      orderType: filters.orderTypes?.length
        ? { id: { in: filters.orderTypes.map((orderType) => Number(orderType.id)) } }
        : undefined,
      warehouse: filters.warehouses?.length ? { id: { in: filters.warehouses.map((w) => w.id) } } : undefined
    };
  }

  toShot(shot: any) {
    return new Shot({
      id: shot.shotId,
      status: new Status({ id: shot.shotApprovalStatusId }),
      mandatory: shot.mandatory,
      imgPath: shot.imgPath,
      isAi: shot.isAi,
      hasHistory: shot.hasHistory,
      selected: shot.selected,
      source: shot.source
        ? new ShotSource({
            orderId: shot.source.orderId,
            date: shot.source.date ? new Date(shot.source.date) : undefined
          })
        : undefined,
      estimatedFileName: shot.estimatedFileName,
      colorId: shot.colorId,
      colorName: shot.colorName,
      modelId: shot.modelId,
      sizeGroup: new SizeGroup({
        id: shot.sizeGroupId || 0,
        name: shot.sizeGroupName || '-'
      }),
      shotApprovalStatus: shot.shotApprovalStatusId,
      code: shot.code || undefined,
      mediaFormat: shot.mediaFormat
        ? new MediaFormat({ id: shot.mediaFormat.id, name: shot.mediaFormat.label })
        : undefined,
      aspectRatio: shot.aspectRatio
        ? new AspectRatio({ id: shot.aspectRatio.id, label: shot.aspectRatio.label })
        : undefined,
      shotType: shot.shotType ? new ShotType({ id: shot.shotType.id, name: shot.shotType.label }) : undefined
    });
  }

  toOrderDetail(orderDetail: any) {
    return new OrderDetail({
      style: this.styleMapper.toStyle(orderDetail.style),
      warehouse: orderDetail.warehouse
        ? new Warehouse({
            id: orderDetail.warehouse.id,
            code: orderDetail.warehouse.code,
            description: orderDetail.warehouse.description
          })
        : undefined,
      colorShots: orderDetail.colorsSizeGroups?.map(
        (csg: any) =>
          new ColorShots({
            color: new Color({
              id: csg.colorId,
              name: csg.colorName,
              size: new Size({ id: csg.size.id, name: csg.size.label }) /*estimatedCost: csg.estimatedCost*/
            }),
            shots: csg.shots?.map((shot: any) => this.toShot(shot)),
            orderStyleId: csg.orderStyleId,
            isAi: csg.isAi
          })
      )
    });
  }

  filtersToUrl(filters: OrderFilters): any {
    return {
      ids: filters.ids || undefined,
      createDateFrom: this.utils.dateToString(filters.creationDate?.from) || undefined,
      createDateTo: this.utils.dateToString(filters.creationDate?.to) || undefined,
      dueDateFrom: this.utils.dateToString(filters.dueDate?.from) || undefined,
      dueDateTo: this.utils.dateToString(filters.dueDate?.to) || undefined,
      completionDateFrom: this.utils.dateToString(filters.completionDate?.from) || undefined,
      completionDateTo: this.utils.dateToString(filters.completionDate?.to) || undefined,
      photographers: filters.photographers?.map((p) => p.id) || undefined,
      statuses: filters.statuses?.map((s) => s.id) || undefined,
      orderTypes: filters.orderTypes?.map((orderType) => orderType.id) || undefined,
      page: filters.page || 1,
      size: filters.size || 20
    };
  }

  urlToFilters(url: any) {
    const toOrderTypeValue = (id: any): OrderType =>
      new OrderType({
        id: Number(id) as OrderTypeEnum,
        name: ''
      });

    return new OrderFilters({
      ids: url.ids ? (Array.isArray(url.ids) ? url.ids.map((id: any) => Number(id)) : [Number(url.ids)]) : undefined,
      dueDate:
        url.dueDateFrom && url.dueDateTo
          ? new DateRange({ from: new Date(url.dueDateFrom), to: new Date(url.dueDateTo) })
          : undefined,
      creationDate:
        url.createDateFrom && url.createDateTo
          ? new DateRange({ from: new Date(url.createDateFrom), to: new Date(url.createDateTo) })
          : undefined,
      completionDate:
        url.completionDateFrom && url.completionDateTo
          ? new DateRange({ from: new Date(url.completionDateFrom), to: new Date(url.completionDateTo) })
          : undefined,
      photographers: url.photographers
        ? Array.isArray(url.photographers)
          ? url.photographers.map((id: any) => new Photographer({ id: Number(id) }))
          : [new Photographer({ id: Number(url.photographers) })]
        : undefined,
      statuses: url.statuses
        ? Array.isArray(url.statuses)
          ? url.statuses.map((id: any) => new Status({ id: Number(id) }))
          : [new Status({ id: Number(url.statuses) })]
        : undefined,
      orderTypes: url.orderTypes
        ? Array.isArray(url.orderTypes)
          ? url.orderTypes.map((id: any) => toOrderTypeValue(id))
          : [toOrderTypeValue(url.orderTypes)]
        : undefined,
      page: url.page ? Number(url.page) : 1,
      size: url.size ? Number(url.size) : 20
    });
  }

  toOrderStatus(orderStatus: any) {
    return new Status({
      id: orderStatus.id,
      name: orderStatus.name
    });
  }
}
